import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { Object as FabricObject } from 'fabric';

import { PERSIST_KEYS } from '@app/app/persist/persist-keys';

export type IconHub = {
  preview: string;
  image: string;
  source_ext: string;
  triggerObject: FabricObject | null; // хранит объект, который инициировал открытие медиа хаба
};

export type ImageHub = {
  preview: string;
  image: string;
  source_ext: string;
  media_type?: string;
  triggerObject: FabricObject | null; // хранит объект, который инициировал открытие медиа хаба
};

/**
 * Состояние генерации изображений
 * Сохраняется в localStorage для восстановления при перезагрузке страницы
 */
export type ImageGenerationState = {
  currentTaskId: string | null; // request_id для SSE
  currentTaskInternalId: string | null; // task_id для cancel
  workspaceId: string | null; // workspace_id для проверки соответствия
  startedAt: number | null; // timestamp начала генерации (для очистки старых записей)
  prompt: string | null; // промпт пользователя
  generatedImage: {
    preview: string;
    image: string;
    source_ext: string;
  } | null; // сгенерированное изображение (для восстановления превью)
  isCancelled: boolean; // флаг отмены генерации (чтобы не показывать изображение после отмены)
  progressStage: 'task_started' | 'generation_in_progress' | 'saving_generation' | null;
};

type MediaHubModalsStore = {
  isImageHubOpen: boolean;
  isIconsHubOpen: boolean;
  icons: IconHub[];
  images: ImageHub[];
  modeImageHub: 'default' | 'background'; // default - icons and images, background - change background images
  triggerObject: FabricObject | null; // хранит объект, который инициировал открытие медиа хаба
  // Состояние генерации изображений
  imageGeneration: ImageGenerationState;
  // Programmatic tab selection for ImageHub (set by onboarding tour)
  initialTab: 'search' | 'upload' | 'favorites' | 'userImages' | 'generate' | null;
  // Programmatic transparent background checkbox selection (set by onboarding tour)
  initialTransparentBackground: boolean | null;
};

type MediaHubModalsActions = {
  setImageHubOpen: (isOpen: MediaHubModalsStore['isImageHubOpen']) => void;
  setIconsHubOpen: (isOpen: MediaHubModalsStore['isIconsHubOpen']) => void;
  setIcons: (icons: IconHub[]) => void;
  setImage: (icons: ImageHub[]) => void;
  setModeImageHub: (mode: MediaHubModalsStore['modeImageHub']) => void;
  clearStore: () => void;
  clearStoreKeepImageGeneration: () => void;
  setTriggerObject: (object: FabricObject | null) => void;
  setInitialTab: (tab: MediaHubModalsStore['initialTab']) => void;
  setInitialTransparentBackground: (
    value: MediaHubModalsStore['initialTransparentBackground'],
  ) => void;
  // Действия для генерации изображений
  setImageGenerationTask: (
    taskId: string,
    taskInternalId: string,
    workspaceId: string,
    prompt: string,
  ) => void;
  clearImageGenerationTask: () => void;
  resetImageGenerationTask: (requestId: string) => void;
  getImageGenerationTask: () => ImageGenerationState;
  setGeneratedImage: (
    requestId: string,
    imageData: { preview: string; image: string; source_ext: string },
  ) => void;
  setCancelled: (requestId: string) => void;
  setProgressStage: (
    requestId: string,
    stage: 'task_started' | 'generation_in_progress' | 'saving_generation',
  ) => void;
};

const defaultImageGenerationState: ImageGenerationState = {
  currentTaskId: null,
  currentTaskInternalId: null,
  workspaceId: null,
  startedAt: null,
  prompt: null,
  generatedImage: null,
  isCancelled: false,
  progressStage: null,
};

const defaultState: MediaHubModalsStore = {
  isImageHubOpen: false,
  isIconsHubOpen: false,
  icons: [],
  images: [],
  modeImageHub: 'default',
  triggerObject: null,
  imageGeneration: defaultImageGenerationState,
  initialTab: null,
  initialTransparentBackground: null,
};

// Тип для persist (исключаем несериализуемые поля)
type PersistedState = Omit<MediaHubModalsStore, 'triggerObject' | 'icons' | 'images'>;

const useMediaHubModalsStore = create<MediaHubModalsStore & MediaHubModalsActions>()(
  persist(
    (set, get) => ({
      ...defaultState,
      setIconsHubOpen: (isOpen) => set(() => ({ isIconsHubOpen: isOpen })),
      setImageHubOpen: (isOpen) => set(() => ({ isImageHubOpen: isOpen })),
      setIcons: (icons) => set(() => ({ icons })),
      setImage: (images) => set(() => ({ images })),
      setModeImageHub: (mode) => set(() => ({ modeImageHub: mode })),
      clearStore: () => set(() => ({ ...defaultState })),
      clearStoreKeepImageGeneration: () =>
        set((state) => ({
          ...defaultState,
          imageGeneration: state.imageGeneration,
        })),
      setTriggerObject: (object) => set(() => ({ triggerObject: object })),
      setInitialTab: (tab) => set(() => ({ initialTab: tab })),
      setInitialTransparentBackground: (value) =>
        set(() => ({ initialTransparentBackground: value })),
      // Действия для генерации изображений
      setImageGenerationTask: (taskId, taskInternalId, workspaceId, prompt) =>
        set(() => ({
          imageGeneration: {
            currentTaskId: taskId,
            currentTaskInternalId: taskInternalId,
            workspaceId,
            startedAt: Date.now(),
            prompt,
            generatedImage: null,
            isCancelled: false,
            progressStage: null,
          },
        })),
      clearImageGenerationTask: () =>
        set(() => ({
          imageGeneration: defaultImageGenerationState,
        })),
      resetImageGenerationTask: (requestId) =>
        set((state) => {
          // Обнуляем только если request_id совпадает
          if (state.imageGeneration.currentTaskId === requestId) {
            return {
              imageGeneration: defaultImageGenerationState,
            };
          }
          return state;
        }),
      getImageGenerationTask: () => get().imageGeneration,
      setGeneratedImage: (requestId, imageData) =>
        set((state) => {
          // Сохраняем изображение только если request_id совпадает и не было отмены
          if (
            state.imageGeneration.currentTaskId === requestId &&
            !state.imageGeneration.isCancelled
          ) {
            return {
              imageGeneration: {
                ...state.imageGeneration,
                generatedImage: imageData,
              },
            };
          }
          return state;
        }),
      setCancelled: (requestId) =>
        set((state) => {
          // Устанавливаем флаг отмены только если request_id совпадает
          if (state.imageGeneration.currentTaskId === requestId) {
            return {
              imageGeneration: {
                ...state.imageGeneration,
                isCancelled: true,
                // Очищаем изображение при отмене
                generatedImage: null,
              },
            };
          }
          return state;
        }),
      setProgressStage: (requestId, stage) =>
        set((state) => {
          if (state.imageGeneration.currentTaskId === requestId) {
            return {
              imageGeneration: {
                ...state.imageGeneration,
                progressStage: stage,
              },
            };
          }
          return state;
        }),
    }),
    {
      name: PERSIST_KEYS.IMAGE_GENERATION_STATE,
      storage: createJSONStorage(() => localStorage),
      // Сохраняем только состояние генерации изображений
      partialize: (state) =>
        ({
          imageGeneration: state.imageGeneration,
        }) as PersistedState,
      // Очищаем старые записи (старше 1 часа)
      merge: (persistedState, currentState) => {
        const parsed = persistedState as PersistedState;
        const imageGeneration = parsed.imageGeneration || defaultImageGenerationState;

        // Очищаем старые записи (старше 1 часа)
        if (imageGeneration.startedAt && Date.now() - imageGeneration.startedAt > 60 * 60 * 1000) {
          return {
            ...currentState,
            imageGeneration: defaultImageGenerationState,
          };
        }

        return {
          ...currentState,
          imageGeneration,
        };
      },
    },
  ),
);

export { useMediaHubModalsStore };
